'use client'

import { useState } from 'react'
import { ChevronDown } from 'lucide-react'
import MovieCard from './movie-card'
import { Movie } from '@/types'

interface MovieGridProps {
  movies: Movie[]
  onMovieClick: (movie: Movie) => void
  userCategories: Record<string, string>
  isFutureLoading?: boolean
  hasActiveFilters?: boolean
}

const isFifaMovie = (movie: Movie) =>
  movie.movieName.toLowerCase().includes('fifa')

export default function MovieGrid({ movies, onMovieClick, userCategories, isFutureLoading, hasActiveFilters }: MovieGridProps) {
  // Track categories that the user explicitly toggled in the current view
  const [userToggles, setUserToggles] = useState<Record<string, boolean>>({})

  // Default expanded sections when NO filters/search are active
  const defaultExpanded: Record<string, boolean> = {
    'starred': true,
    'uncategorized': true,
  }

  const toggleCategory = (category: string) => {
    setUserToggles(prev => {
      const currentlyExpanded = getIsExpanded(category, prev)
      return {
        ...prev,
        [category]: !currentlyExpanded,
      }
    })
  }

  const getIsExpanded = (category: string, toggles: Record<string, boolean> = userToggles) => {
    if (category in toggles) {
      return toggles[category]
    }
    // When search/filters are active, expand all categories with results by default
    if (hasActiveFilters) {
      return true
    }
    return Boolean(defaultExpanded[category])
  }

  const categorizeMovies = () => {
    const categorized: Record<string, Movie[]> = {
      'starred': [],
      'upcoming-starred': [],
      'uncategorized': [],
      'watch-again': [],
      'upcoming-watch-again': [],
      'fifa': [],
      'upcoming': [],
      'watched': [],
      'ignored': [],
    }

    movies.forEach(movie => {
      // FIFA movies always go into their own bucket regardless of other categories
      if (isFifaMovie(movie)) {
        categorized['fifa'].push(movie)
        return
      }

      // Check user category
      const userCategory = userCategories[movie.movieId]
      if (userCategory === 'watched') {
        categorized['watched'].push(movie)
      } else if (userCategory === 'ignored') {
        categorized['ignored'].push(movie)
      } else if (userCategory === 'starred') {
        if (movie.isUpcoming) {
          categorized['upcoming-starred'].push(movie)
        } else {
          categorized['starred'].push(movie)
        }
      } else if (userCategory === 'watch-again') {
        if (movie.isUpcoming) {
          categorized['upcoming-watch-again'].push(movie)
        } else {
          categorized['watch-again'].push(movie)
        }
      } else if (movie.isUpcoming) {
        categorized['upcoming'].push(movie)
      } else {
        categorized['uncategorized'].push(movie)
      }
    })

    // Sort upcoming categories by earliest showtime date ascending
    const upcomingKeys = ['upcoming', 'upcoming-starred', 'upcoming-watch-again']
    upcomingKeys.forEach(key => {
      categorized[key].sort((a, b) => {
        const getEarliestTime = (m: Movie) => {
          if (!m.showtimes || m.showtimes.length === 0) return Infinity
          return Math.min(...m.showtimes.map(s => {
            const date = s.showDateTimeUtc ? new Date(s.showDateTimeUtc) : new Date(s.showDateTimeLocal)
            return date.getTime()
          }))
        }
        return getEarliestTime(a) - getEarliestTime(b)
      })
    })

    return categorized
  }

  const categorized = categorizeMovies()

  const categoryConfig = [
    { key: 'starred',             label: 'Starred (Now Playing)', accent: 'bg-blue-600' },
    { key: 'uncategorized',       label: 'Now Playing',          accent: 'bg-slate-700' },
    { key: 'upcoming-starred',    label: '⭐ Upcoming Starred',    accent: 'bg-indigo-500' },
    { key: 'upcoming',            label: 'Upcoming',             accent: 'bg-amber-500' },
    { key: 'watch-again',         label: 'Watch Again',          accent: 'bg-purple-600' },
    { key: 'upcoming-watch-again',label: 'Upcoming Watch Again', accent: 'bg-fuchsia-500' },
    { key: 'fifa',                label: '⚽ FIFA',              accent: 'bg-emerald-600' },
    { key: 'watched',             label: 'Watched',              accent: 'bg-green-600' },
    { key: 'ignored',             label: 'Ignored',              accent: 'bg-red-500' },
  ]

  return (
    <div className="space-y-2 sm:space-y-6">
      {categoryConfig.map(({ key, label, accent }) => {
        const categoryMovies = categorized[key] || []
        if (categoryMovies.length === 0) return null
        const isExpanded = getIsExpanded(key)

        return (
          <div key={key} className="overflow-hidden sm:rounded-xl sm:border sm:border-slate-200 sm:bg-white sm:shadow-sm">
            {/* Section header — compact bar on mobile, slightly taller on desktop */}
            <button
              onClick={() => toggleCategory(key)}
              className="flex items-center justify-between w-full px-3 py-2 sm:px-5 sm:py-3 bg-slate-50 sm:bg-slate-50 border-b border-slate-200 hover:bg-slate-100 transition-colors"
            >
              <div className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full shrink-0 ${accent}`} />
                <span className="text-sm font-semibold text-slate-800 tracking-tight">{label}</span>
                <span className="text-xs text-slate-400 font-normal">({categoryMovies.length})</span>
              </div>
              <ChevronDown
                size={16}
                className={`text-slate-400 transition-transform duration-200 ${isExpanded ? '' : '-rotate-90'}`}
              />
            </button>

            {isExpanded && (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-1.5 p-1.5 bg-slate-100 sm:bg-transparent sm:p-4 sm:gap-4">
                {categoryMovies.map(movie => (
                  <div key={movie.movieId} className="bg-white sm:bg-transparent rounded sm:overflow-visible overflow-hidden h-full">
                    <MovieCard
                      movie={movie}
                      onClick={() => onMovieClick(movie)}
                      isFutureLoading={isFutureLoading}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
